import { useEffect, useRef } from "react";

export default function FrostReveal({ images = [], onProgress }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const lastSampleRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    lastSampleRef.current = 0;

    const paint = () => {
      const w = wrap.offsetWidth;
      const h = wrap.offsetHeight;
      canvas.width = w;
      canvas.height = h;
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, "rgba(9,11,18,0.94)");
      grad.addColorStop(1, "rgba(18,16,28,0.94)");
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      for (let i = 0; i < w * h / 2200; i++) {
        ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.05})`;
        ctx.fillRect(Math.random() * w, Math.random() * h, 2, 2);
      }
    };
    paint();

    // Measure the TRUE cleared fraction by sampling the canvas alpha channel.
    const sampleProgress = () => {
      const w = canvas.width, h = canvas.height;
      if (!w || !h) return;
      const { data } = ctx.getImageData(0, 0, w, h);
      let clear = 0, total = 0;
      for (let i = 3; i < data.length; i += 4 * 20) { // sample every 20th pixel
        total++;
        if (data[i] < 25) clear++;
      }
      const p = total ? clear / total : 0;
      onProgress && onProgress(p);
    };

    const erase = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      ctx.globalCompositeOperation = "destination-out";
      const r = 58;
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, "rgba(0,0,0,1)");
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();

      const now = Date.now();
      if (now - lastSampleRef.current > 250) {
        lastSampleRef.current = now;
        sampleProgress();
      }
    };

    const onMove = (e) => erase(e.clientX, e.clientY);
    const onTouch = (e) => { for (const t of e.touches) erase(t.clientX, t.clientY); };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("touchmove", onTouch, { passive: true });
    window.addEventListener("resize", paint);
    return () => {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("touchmove", onTouch);
      window.removeEventListener("resize", paint);
    };
  }, [images, onProgress]);

  return (
    <div ref={wrapRef} className="absolute inset-0 overflow-hidden" data-testid="frost-reveal">
      <div className="absolute inset-0 grid grid-cols-2">
        {images.map((src, i) => (
          <div key={i} className="relative h-full overflow-hidden">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ touchAction: "none" }} />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent pointer-events-none" />
    </div>
  );
}
